import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Modal, Button } from '../../components';
import { apiBaseUrl } from '../../util/api';

import css from './NewsletterModal.module.css';

const NewsletterModal = props => {
  const { 
    id, 
    isOpen, 
    onClose, 
    onManageDisableScrolling,
    className,
    rootClassName 
  } = props;
  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate first name
    if (!formData.firstName.trim()) {
      newErrors.firstName = intl.formatMessage({
        id: 'NewsletterModal.firstNameRequired',
        defaultMessage: 'First name is required',
      });
    }

    // Validate last name
    if (!formData.lastName.trim()) {
      newErrors.lastName = intl.formatMessage({
        id: 'NewsletterModal.lastNameRequired',
        defaultMessage: 'Last name is required',
      });
    }

    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = intl.formatMessage({
        id: 'NewsletterModal.emailRequired',
        defaultMessage: 'Email is required',
      });
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = intl.formatMessage({
          id: 'NewsletterModal.emailInvalid',
          defaultMessage: 'Please enter a valid email address',
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      // Use the correct API URL based on environment (same as footer)
      const response = await fetch(`${apiBaseUrl()}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        credentials: 'include', // Include cookies for CORS
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus('success');
        
        // Check if it's a duplicate subscription
        if (data.duplicate) {
          setSubmitStatus('info');
          setSubmitMessage(
            data.message ||
            intl.formatMessage({
              id: 'NewsletterModal.alreadySubscribed',
              defaultMessage: 'You are already subscribed to our newsletter',
            })
          );
        } else {
          setSubmitStatus('success');
          setSubmitMessage(
            data.message ||
            intl.formatMessage({
              id: 'NewsletterModal.successMessage',
              defaultMessage: 'Thank you for subscribing! You will be notified when new resources are available.',
            })
          );
        }
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
        });
      } else {
        setSubmitStatus('error');
        setSubmitMessage(
          data.message ||
          intl.formatMessage({
            id: 'NewsletterModal.errorMessage',
            defaultMessage: 'Something went wrong. Please try again later.',
          })
        );
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setSubmitStatus('error');
      setSubmitMessage(
        intl.formatMessage({
          id: 'NewsletterModal.networkError',
          defaultMessage: 'Network error. Please check your connection and try again.',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form and status when closing
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
    });
    setErrors({});
    setSubmitStatus(null);
    setSubmitMessage('');
    onClose();
  };

  return (
    <Modal
      id={id}
      containerClassName={classes}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={intl.formatMessage({
        id: 'NewsletterModal.closeModal',
        defaultMessage: 'Close newsletter subscription modal',
      })}
    >
      <h2 className={css.modalTitle}>
        <FormattedMessage
          id="NewsletterModal.title"
          defaultMessage="Stay Updated!"
        />
      </h2>
        
        <p className={css.modalDescription}>
          <FormattedMessage
            id="NewsletterModal.description"
            defaultMessage="Subscribe to our newsletter and be the first to know when new learning resources are available."
          />
        </p>

        {submitStatus === 'success' && (
          <div className={css.successMessage}>
            {submitMessage}
          </div>
        )}

        {submitStatus === 'info' && (
          <div className={css.infoMessage}>
            {submitMessage}
          </div>
        )}

        {submitStatus === 'error' && (
          <div className={css.errorMessageBox}>
            {submitMessage}
          </div>
        )}

        {submitStatus !== 'success' && submitStatus !== 'info' && (
          <form onSubmit={handleSubmit} className={css.form}>
            <div className={css.inputGroup}>
              <label htmlFor="firstName" className={css.label}>
                <FormattedMessage
                  id="NewsletterModal.firstNameLabel"
                  defaultMessage="First Name"
                />
                <span style={{ color: '#ef4444' }}> *</span>
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleInputChange}
                className={classNames(css.input, {
                  [css.inputError]: errors.firstName,
                })}
                placeholder={intl.formatMessage({
                  id: 'NewsletterModal.firstNamePlaceholder',
                  defaultMessage: 'Enter your first name',
                })}
                disabled={loading}
              />
              {errors.firstName && (
                <span className={css.errorMessage}>{errors.firstName}</span>
              )}
            </div>

            <div className={css.inputGroup}>
              <label htmlFor="lastName" className={css.label}>
                <FormattedMessage
                  id="NewsletterModal.lastNameLabel"
                  defaultMessage="Last Name"
                />
                <span style={{ color: '#ef4444' }}> *</span>
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleInputChange}
                className={classNames(css.input, {
                  [css.inputError]: errors.lastName,
                })}
                placeholder={intl.formatMessage({
                  id: 'NewsletterModal.lastNamePlaceholder',
                  defaultMessage: 'Enter your last name',
                })}
                disabled={loading}
              />
              {errors.lastName && (
                <span className={css.errorMessage}>{errors.lastName}</span>
              )}
            </div>

            <div className={css.inputGroup}>
              <label htmlFor="email" className={css.label}>
                <FormattedMessage
                  id="NewsletterModal.emailLabel"
                  defaultMessage="Email Address"
                />
                <span style={{ color: '#ef4444' }}> *</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className={classNames(css.input, {
                  [css.inputError]: errors.email,
                })}
                placeholder={intl.formatMessage({
                  id: 'NewsletterModal.emailPlaceholder',
                  defaultMessage: 'Enter your email address',
                })}
                disabled={loading}
              />
              {errors.email && (
                <span className={css.errorMessage}>{errors.email}</span>
              )}
            </div>

            <button
              type="submit"
              className={css.submitButton}
              disabled={loading}
            >
              {loading ? (
                <FormattedMessage
                  id="NewsletterModal.submitting"
                  defaultMessage="Subscribing..."
                />
              ) : (
                <FormattedMessage
                  id="NewsletterModal.submitButton"
                  defaultMessage="Subscribe"
                />
              )}
            </button>
          </form>
        )}

        {(submitStatus === 'success' || submitStatus === 'info') && (
          <Button
            onClick={handleClose}
            className={css.closeButton}
          >
            <FormattedMessage
              id="NewsletterModal.closeButton"
              defaultMessage="Close"
            />
          </Button>
        )}
      </Modal>
    );
  };

NewsletterModal.defaultProps = {
  className: null,
  rootClassName: null,
};

NewsletterModal.propTypes = {
  id: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  className: PropTypes.string,
  rootClassName: PropTypes.string,
};

export default NewsletterModal;
